import { ProjectSection, ProjectSectionProps } from '../../model/ProjectSection';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import Slider, { Settings } from 'react-slick';
import { Section } from '../Section';
import { Container } from '../Container';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styled from 'styled-components';
import { Colors } from '../../constants/colors';
import { useIsMobile } from '../../helpers/useIsMobile';
import { references } from '../../config/referencesConfig';
import { Reference } from '../../model/Reference';
import { ArrowRightIcon } from '../icons/ArrowRightIcon';
import { ArrowLeftIcon } from '../icons/ArrowLeftIcon';
import { Trans, useTranslation } from 'react-i18next';
import { isPrerendering } from '../../helpers/isPrerendering';
import { ArrowDownIcon } from '../icons/ArrowDownIcon';
import { PersonIcon } from '../icons/PersonIcon';

type ReferencesProps = ProjectSectionProps & {
  /** Defaults to the slp.blue testimonials shown on the home page. */
  items?: Reference[];
  /** Where the position, company and text of each item are translated. */
  translationPrefix?: string;
  headingKey?: string;
  id?: ProjectSection;
  /** Labels of the toggle shown on testimonials that have a full text. */
  fullTextLabels?: FullTextLabels;
};

type FullTextLabels = {
  show: string;
  hide: string;
};

export const References = (props: ReferencesProps): ReactElement | null => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const items = props.items ?? references;
  const prefix = props.translationPrefix ?? 'references';
  // A single testimonial is shown on its own, without carousel controls.
  const hasMultiple = items.length > 1;
  // react-slick sets inline styles that the browser serializes differently than
  // React expects, so it can't hydrate against the prerendered HTML. Render the
  // slides statically for prerender + first client render (keeps testimonials in
  // the static HTML and hydrates cleanly), then mount the real carousel.
  const [mounted, setMounted] = useState<boolean>(false);
  const sliderRef = useRef<Slider>(null);
  useEffect(() => {
    if (!isPrerendering()) {
      setMounted(true);
    }
  }, []);

  const carouselSettings: Settings = {
    dots: hasMultiple,
    infinite: hasMultiple,
    speed: 500,
    autoplaySpeed: 5000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: !isMobile && hasMultiple,
    autoplay: hasMultiple,
    pauseOnHover: true,
    nextArrow: <CustomNextArrow />,
    prevArrow: <CustomPrevArrow />,
  };

  // An expanded full text takes a while to read, so the carousel must not move
  // on in the meantime; it resumes once the text is collapsed again.
  const onExpandedChange = (expanded: boolean) => (expanded ? sliderRef.current?.slickPause() : sliderRef.current?.slickPlay());

  const renderCard = (item: Reference, index: number) => (
    <ReferenceCard key={index} item={item} prefix={prefix} fullTextLabels={props.fullTextLabels} onExpandedChange={hasMultiple ? onExpandedChange : undefined} />
  );

  return (
    <Section id={props.id ?? 'references'} inverse={props.inverse} heading={t(props.headingKey ?? 'section.references')}>
      <Container>
        <div>
          <CarouselWrapper>
            {mounted ? (
              <Slider ref={sliderRef} {...carouselSettings}>
                {items.map((item, index) => (
                  <div key={index}>{renderCard(item, index)}</div>
                ))}
              </Slider>
            ) : (
              <StaticTrack>
                {items.map((item, index) => (
                  <StaticSlide key={index}>{renderCard(item, index)}</StaticSlide>
                ))}
              </StaticTrack>
            )}
          </CarouselWrapper>
        </div>
      </Container>
    </Section>
  );
};

type ReferenceCardProps = {
  item: Reference;
  prefix: string;
  fullTextLabels?: FullTextLabels;
  onExpandedChange?: (expanded: boolean) => void;
};

const ReferenceCard = ({ item, prefix, fullTextLabels, onExpandedChange }: ReferenceCardProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(false);
  const key = `${prefix}.${item.key}`;
  const fullTextKey = `${key}.fullText`;
  const hasFullText = !!fullTextLabels && i18n.exists(fullTextKey);
  const fullTextId = `reference-${item.key}-full`;
  const fullText = t(fullTextKey, { returnObjects: true }) as string[];

  return (
    <ReferenceItem>
      <ReferrerInfo>
        <ReferrerImg>{item.photo ? <img src={`/references/${item.photo}`} alt={item.photo} /> : <PersonIcon color={Colors.primary} size={64} />}</ReferrerImg>
        <h3>{item.name}</h3>
        {i18n.exists(`${key}.position`) && <div>{t(`${key}.position`)}</div>}
        {i18n.exists(`${key}.company`) && <CompanyText>{t(`${key}.company`)}</CompanyText>}
      </ReferrerInfo>
      <ReferenceBody>
        {expanded ? (
          <FullText id={fullTextId}>
            {i18n.exists(`${key}.fullTitle`) && <h4>{t(`${key}.fullTitle`)}</h4>}
            {fullText.map((_, index) => (
              <p key={index}>
                <Trans i18nKey={`${fullTextKey}.${index}`} components={{ strong: <strong /> }} />
              </p>
            ))}
          </FullText>
        ) : (
          <ReferenceText>
            <span>
              {'"'}
              <Trans i18nKey={`${key}.text`} components={{ strong: <strong /> }} />
              {'"'}
            </span>
          </ReferenceText>
        )}
        {hasFullText && (
          <FullTextToggle
            type="button"
            onClick={() => {
              setExpanded(!expanded);
              onExpandedChange?.(!expanded);
            }}
            aria-expanded={expanded}
            aria-controls={expanded ? fullTextId : undefined}
            expanded={expanded}
          >
            <span>{expanded ? fullTextLabels.hide : fullTextLabels.show}</span>
            <ArrowDownIcon color={Colors.white} size={20} strokeWidth={5} />
          </FullTextToggle>
        )}
      </ReferenceBody>
    </ReferenceItem>
  );
};

function CustomPrevArrow(props: object) {
  return (
    <ArrowWrapper {...props}>
      <ArrowRightIcon size={30} color={Colors.primary} />
    </ArrowWrapper>
  );
}

function CustomNextArrow(props: object) {
  return (
    <ArrowWrapper {...props}>
      <ArrowLeftIcon size={30} color={Colors.primary} />
    </ArrowWrapper>
  );
}

const ArrowWrapper = styled.div`
  &::before {
    display: none;
  }
`;

const ReferenceBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 15px;
`;

const ReferenceText = styled.div`
  flex: 1;
  font-style: italic;
  display: flex;
  align-items: center;

  @media (max-width: 726px) {
    text-align: center;
    align-items: start;
  }
`;

const FullText = styled.div`
  h4 {
    margin: 0 0 10px;
    font-size: 18px;
  }

  p {
    margin: 0 0 12px;
    /* Overrides the centred paragraph styling of Section; a letter reads left aligned. */
    text-align: left !important;
    line-height: 1.8;
  }
`;

const FullTextToggle = styled.button<{ expanded: boolean }>`
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  background: transparent;
  border: none;
  color: ${Colors.white};
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;

  svg {
    transition: transform 0.2s ease-in-out;
    transform: ${(p) => (p.expanded ? 'rotate(180deg)' : 'none')};
  }

  @media (max-width: 726px) {
    align-self: center;
  }
`;

const CompanyText = styled.div`
  font-weight: 600;
`;

const ReferenceItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  gap: 40px;
  height: 100%;

  @media (max-width: 726px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
`;

const ReferrerInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  h3 {
    text-align: center;
    margin-top: 10px;
    margin-bottom: 5px;
  }

  div {
    text-align: center;
  }

  @media (max-width: 726px) {
    padding-right: 0;
  }
`;

const ReferrerImg = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  /* Frames the placeholder icon shown when there is no photo. */
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Colors.primaryLight};

  img {
    width: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

// Static pre-hydration version of the carousel: shows the first slide (the
// rest are kept in the DOM for SEO but clipped), so it matches the mounted
// carousel's height and avoids a layout shift when the carousel takes over.
const StaticTrack = styled.div`
  display: flex;
  overflow: hidden;
`;

const StaticSlide = styled.div`
  flex: 0 0 100%;
  height: auto;
  padding: 30px;
  background: ${Colors.primary};
  color: ${Colors.white};
  border-radius: 20px;
  margin-right: 10px;
  margin-left: 10px;
`;

const CarouselWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 10px;

  // Equal-height slides via CSS (instead of measuring the DOM in JS, which
  // broke hydration: the prerendered markup baked in a measured pixel height
  // that the client's initial render did not have).
  .slick-track {
    display: flex;
  }

  .slick-slide {
    height: auto;
  }

  .slick-slide > div {
    height: 100%;
    padding: 30px;
    background: ${Colors.primary};
    color: ${Colors.white};
    border-radius: 20px;
    margin-right: 10px;
    margin-left: 10px;
  }

  .slick-prev,
  .slick-next {
    width: 30px;
    height: 30px;
    z-index: 1;
  }

  @media (max-width: 726px) {
    padding: 0;
    .slick-prev,
    .slick-next {
      display: none;
    }
  }

  .slick-prev {
    left: -35px;
  }

  .slick-next {
    right: -35px;
  }

  .slick-prev:before,
  .slick-next:before {
    opacity: 1;
    color: ${Colors.primary};
    font-size: 30px;
  }

  .slick-prev:hover:before,
  .slick-next:hover:before {
    color: ${Colors.primaryDark};
  }

  .slick-dots {
    bottom: unset;
    top: 100%;
    margin-top: 10px;
  }

  .slick-dots li button:before {
    font-size: 10px;
    opacity: 0.25;
    color: ${Colors.primary};
  }

  .slick-dots li.slick-active button:before {
    opacity: 1;
    color: ${Colors.primary};
  }

  .slick-dots li button:hover:before {
    opacity: 1;
    color: ${Colors.primaryDark};
  }
`;
